import React, { useState, useRef, useCallback, useMemo, ReactElement } from 'react';
import {
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Box,
  useTheme,
} from '@mui/material';
import { useCompanyPnlForecastLazyQuery, PnlYearRowType } from '../../../../gql/generated/graphql';

type Magnitude = { divisor: number; label: string };

function detectMagnitude(rows: PnlYearRowType[]): Magnitude {
  let maxAbs = 0;
  for (const r of rows) {
    for (const v of [r.revenue, r.ebit, r.ebitda, r.grossProfit]) {
      if (v != null && Math.abs(v) > maxAbs) maxAbs = Math.abs(v);
    }
  }
  if (maxAbs >= 1_000_000) return { divisor: 1_000, label: 'in EUR m' };
  if (maxAbs >= 1_000) return { divisor: 1, label: "in EUR '000" };
  return { divisor: 1, label: "in EUR '000" };
}

const fmtNum = (v: number | null | undefined, divisor: number): string => {
  if (v == null) return '-';
  const scaled = v / divisor;
  const abs = Math.abs(scaled);
  const decimals = abs < 10 ? 1 : 0;
  const formatted = abs.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return scaled < 0 ? `(${formatted})` : formatted;
};

const pct = (v: number | null | undefined): string => {
  if (v == null) return '-';
  return `${(v * 100).toFixed(1)}%`;
};

const marginPct = (num: number | null | undefined, den: number | null | undefined): string => {
  if (num == null || den == null || den === 0) return '';
  return `${((num / den) * 100).toFixed(0)}%`;
};

interface PnlForecastPopoverProps {
  companyId: number;
  children: ReactElement;
}

type RowDef = {
  label: string;
  getValue: (r: PnlYearRowType, divisor: number) => string;
  getMargin?: (r: PnlYearRowType) => string | null;
  bold?: boolean;
};

const ROW_DEFS: RowDef[] = [
  { label: 'Revenue', getValue: (r, d) => fmtNum(r.revenue, d), bold: true },
  { label: 'COGS', getValue: (r, d) => fmtNum(r.cogs, d) },
  {
    label: 'Gross Profit',
    getValue: (r, d) => fmtNum(r.grossProfit, d),
    getMargin: r => marginPct(r.grossProfit, r.revenue) || null,
  },
  { label: 'OpEx', getValue: (r, d) => fmtNum(r.opex, d) },
  {
    label: 'EBITDA',
    getValue: (r, d) => fmtNum(r.ebitda, d),
    getMargin: r => marginPct(r.ebitda, r.revenue) || null,
    bold: true,
  },
  { label: 'D&A', getValue: (r, d) => fmtNum(r.da, d) },
  {
    label: 'EBIT',
    getValue: (r, d) => fmtNum(r.ebit, d),
    getMargin: r => marginPct(r.ebit, r.revenue) || null,
    bold: true,
  },
  { label: 'FTE', getValue: r => (r.fte != null && r.fte > 0 ? r.fte.toFixed(0) : '-') },
  { label: 'Rev. Growth', getValue: r => pct(r.revenueGrowth) },
];

export default function PnlForecastPopover({ companyId, children }: PnlForecastPopoverProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [fetchPnl, { data, loading, called }] = useCompanyPnlForecastLazyQuery();

  const clearLeaveTimer = useCallback(() => {
    if (leaveTimer.current) {
      clearTimeout(leaveTimer.current);
      leaveTimer.current = null;
    }
  }, []);

  const handleEnter = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      clearLeaveTimer();
      setAnchorEl(e.currentTarget);
      if (!called) {
        fetchPnl({ variables: { companyId } });
      }
    },
    [called, companyId, fetchPnl, clearLeaveTimer]
  );

  const handleLeave = useCallback(() => {
    clearLeaveTimer();
    leaveTimer.current = setTimeout(() => setAnchorEl(null), 200);
  }, [clearLeaveTimer]);

  const handlePopoverEnter = useCallback(() => {
    clearLeaveTimer();
  }, [clearLeaveTimer]);

  const handlePopoverLeave = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const open = Boolean(anchorEl);
  const rows = data?.companyPnlForecast?.rows?.filter(Boolean) as PnlYearRowType[] | undefined;

  const magnitude = useMemo(() => (rows ? detectMagnitude(rows) : null), [rows]);

  const forecastBg = isDark ? 'rgba(33, 150, 243, 0.12)' : 'rgba(33, 150, 243, 0.06)';
  const paperBg = theme.palette.background.paper;
  const borderColor = theme.palette.divider;

  const cellSx = {
    fontSize: '0.7rem',
    px: 0.75,
    py: 0.25,
    whiteSpace: 'nowrap' as const,
    fontFamily: 'monospace',
    textAlign: 'right' as const,
    borderBottom: `1px solid ${borderColor}`,
  };

  const headerCellSx = {
    ...cellSx,
    fontWeight: 600,
    fontFamily: 'inherit',
    borderBottom: `2px solid ${borderColor}`,
  };

  const labelCellSx = {
    ...cellSx,
    textAlign: 'left' as const,
    fontWeight: 500,
    fontFamily: 'inherit',
    position: 'sticky' as const,
    left: 0,
    backgroundColor: paperBg,
    zIndex: 1,
  };

  return (
    <>
      {React.cloneElement(children, {
        onMouseEnter: handleEnter,
        onMouseLeave: handleLeave,
      })}
      <Popover
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableRestoreFocus
        sx={{ pointerEvents: 'none' }}
        slotProps={{
          paper: {
            sx: { pointerEvents: 'auto', maxWidth: 750, overflow: 'auto' },
            onMouseEnter: handlePopoverEnter,
            onMouseLeave: handlePopoverLeave,
          },
        }}
      >
        <Box sx={{ p: 1.5 }}>
          {loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, px: 2 }}>
              <CircularProgress size={16} />
              <Typography variant="caption">Loading P&L forecast...</Typography>
            </Box>
          )}
          {!loading && (!rows || rows.length === 0) && called && (
            <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
              (no benchmark data)
            </Typography>
          )}
          {!loading && rows && rows.length > 0 && magnitude && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, px: 0.75 }}>
                {magnitude.label}
              </Typography>
              <TableContainer>
                <Table size="small" sx={{ minWidth: 'auto' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...headerCellSx, textAlign: 'left' }}></TableCell>
                      {rows.map(r => (
                        <TableCell
                          key={r.year}
                          sx={{
                            ...headerCellSx,
                            backgroundColor: r.isForecast ? forecastBg : undefined,
                          }}
                        >
                          {r.year}
                          {r.isForecast ? 'F' : ''}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ROW_DEFS.flatMap(def => {
                      const mainRow = (
                        <TableRow key={def.label}>
                          <TableCell
                            sx={{
                              ...labelCellSx,
                              fontWeight: def.bold ? 600 : 500,
                            }}
                          >
                            {def.label}
                          </TableCell>
                          {rows.map(r => (
                            <TableCell
                              key={r.year}
                              sx={{
                                ...cellSx,
                                backgroundColor: r.isForecast ? forecastBg : undefined,
                                fontWeight: def.bold ? 600 : 400,
                              }}
                            >
                              {def.getValue(r, magnitude.divisor)}
                            </TableCell>
                          ))}
                        </TableRow>
                      );
                      if (!def.getMargin) return [mainRow];
                      const marginRow = (
                        <TableRow key={`${def.label}-margin`}>
                          <TableCell
                            sx={{
                              ...labelCellSx,
                              borderBottom: 'none',
                              py: 0,
                            }}
                          />
                          {rows.map(r => (
                            <TableCell
                              key={r.year}
                              sx={{
                                ...cellSx,
                                fontSize: '0.6rem',
                                opacity: 0.6,
                                borderBottom: 'none',
                                py: 0,
                                backgroundColor: r.isForecast ? forecastBg : undefined,
                              }}
                            >
                              {def.getMargin!(r) || ''}
                            </TableCell>
                          ))}
                        </TableRow>
                      );
                      return [mainRow, marginRow];
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Box>
      </Popover>
    </>
  );
}
